import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';

/**
 * Reveals its children once they scroll into view.
 *
 * Uses a single IntersectionObserver and a CSS animation rather than a motion
 * library, so scroll reveals cost no extra JavaScript on the landing page. The
 * observer disconnects after the first intersection — these animations only
 * ever play once.
 */
export default function Reveal({
  as: Component = 'div',
  delay = 0,
  className,
  children,
  ...props
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // No IntersectionObserver (or reduced motion): show immediately.
    if (
      typeof IntersectionObserver === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Component
      ref={ref}
      style={delay ? { animationDelay: `${delay}ms` } : undefined}
      className={cn(visible ? 'animate-fade-up' : 'opacity-0', className)}
      {...props}
    >
      {children}
    </Component>
  );
}
